import { useState } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { COST_WINDOWS } from '@/sections/settings/subscription/constant';
import { cn } from '@/utils/classname';

export function DateRangePicker({
  selectedDays,
  customFrom,
  customTo,
  onPreset,
  onCustomFrom,
  onCustomTo,
  minDate,
}: {
  selectedDays: number | null;
  customFrom: string;
  customTo: string;
  onPreset: (days: number) => void;
  onCustomFrom: (v: string) => void;
  onCustomTo: (v: string) => void;
  minDate?: string;
}) {
  const [open, setOpen] = useState(false);
  const isCustom = customFrom !== '' && customTo !== '';

  const label = isCustom
    ? `${customFrom} — ${customTo}`
    : selectedDays
      ? (COST_WINDOWS.find(w => w.days === selectedDays)?.label ??
        `${selectedDays}d`)
      : 'Select range';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'border-chariot-stroke text-chariot-text-primary flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium',
            'hover:bg-chariot-background-secondary transition-colors'
          )}
        >
          <svg
            className="text-chariot-text-muted h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="z-[200] w-auto p-0">
        <div className="flex">
          <div className="border-chariot-stroke flex flex-col border-r py-2">
            {COST_WINDOWS.map(w => (
              <button
                key={w.days}
                onClick={() => {
                  onPreset(w.days);
                  setOpen(false);
                }}
                className={cn(
                  'px-4 py-1.5 text-left text-xs font-medium transition-colors',
                  selectedDays === w.days && !isCustom
                    ? 'bg-chariot-brand-primary text-primary-foreground'
                    : 'text-chariot-text-secondary hover:bg-chariot-background-secondary'
                )}
              >
                Last {w.label}
              </button>
            ))}
            {isCustom && (
              <div className="bg-chariot-brand-primary/10 text-chariot-brand-primary px-4 py-1.5 text-xs font-medium">
                Custom
              </div>
            )}
          </div>
          <div className="flex flex-col gap-3 p-4">
            <p className="text-chariot-text-muted text-xs font-semibold tracking-wide uppercase">
              Custom Range
            </p>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="cost-range-from"
                className="text-chariot-text-secondary text-xs"
              >
                From
              </label>
              <input
                id="cost-range-from"
                type="date"
                value={customFrom}
                min={minDate}
                max={customTo || undefined}
                onChange={e => onCustomFrom(e.target.value)}
                className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary rounded-md border px-2 py-1 text-xs outline-hidden"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="cost-range-to"
                className="text-chariot-text-secondary text-xs"
              >
                To
              </label>
              <input
                id="cost-range-to"
                type="date"
                value={customTo}
                min={customFrom || undefined}
                onChange={e => onCustomTo(e.target.value)}
                className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary rounded-md border px-2 py-1 text-xs outline-hidden"
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
